/**
 * Member Bet History Tab
 * แสดงประวัติการเดิมพันของสมาชิก
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Gamepad2,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  Coins,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { BetHistoryService } from '@/domain/bet-history';
import type { BetHistoryItem, BetHistorySummary } from '@/domain/bet-history';
import { useCurrency } from '@/stores/currency-store';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

interface MemberBetHistoryTabProps {
  memberId: string;
}

export function MemberBetHistoryTab({ memberId }: MemberBetHistoryTabProps) {
  const { formatCredit } = useCurrency();
  
  // State
  const [betHistory, setBetHistory] = useState<BetHistoryItem[]>([]);
  const [summary, setSummary] = useState<BetHistorySummary | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const hasMore = page * limit < total;

  // Load bet history
  const loadBetHistory = useCallback(async (pageNum: number = 1) => {
    setIsLoading(true);
    try {
      const filters = selectedProvider !== 'all' ? { provider: selectedProvider } : {};
      
      const result = await BetHistoryService.getMemberBetHistory(memberId, {
        limit,
        skip: (pageNum - 1) * limit,
        filters,
      });
      
      setBetHistory(result.data);
      setTotal(result.pagination.total);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load bet history:', error);
      toast.error('ไม่สามารถโหลดประวัติการเดิมพันได้');
    } finally {
      setIsLoading(false);
    }
  }, [memberId, selectedProvider]);

  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const summaryData = await BetHistoryService.getMemberBetSummary(memberId);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load bet summary:', error);
    }
  }, [memberId]);

  // Load providers
  const loadProviders = useCallback(async () => {
    try {
      const providerList = await BetHistoryService.getBetHistoryProviders(memberId);
      setProviders(providerList);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  }, [memberId]);

  // Refresh bet history from API
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await BetHistoryService.refreshMemberBetHistory(memberId, 7);
      toast.success(`รีเฟรชสำเร็จ: ${result.synced} รายการ`);
      
      // Reload data
      await Promise.all([loadBetHistory(1), loadSummary()]);
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('ไม่สามารถรีเฟรชได้');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadBetHistory(1);
    loadSummary();
    loadProviders();
  }, [loadBetHistory, loadSummary, loadProviders]);

  // Reload when provider filter changes
  useEffect(() => {
    loadBetHistory(1);
  }, [selectedProvider, loadBetHistory]);

  // Format date
  const formatDate = (date: Date | string) => {
    return format(new Date(date), 'dd/MM/yy HH:mm', { locale: th });
  };

  return (
    <div className="space-y-4">
      {/* Header + Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gamepad2 className="w-5 h-5 text-purple-500" />
          <h3 className="font-medium">ประวัติการเดิมพัน</h3>
          {total > 0 && (
            <Badge variant="secondary" className="text-xs">
              {total.toLocaleString()} รายการ
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          รีเฟรช
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20">
            <CardContent className="p-3 text-center">
              <Target className="w-5 h-5 text-blue-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-blue-600">
                {summary.gameCount.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">จำนวนเกม</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20">
            <CardContent className="p-3 text-center">
              <Coins className="w-5 h-5 text-amber-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-amber-600">
                {formatCredit(summary.totalBet)}
              </div>
              <div className="text-xs text-muted-foreground">ยอดเดิมพัน</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20">
            <CardContent className="p-3 text-center">
              <Trophy className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-emerald-600">
                {formatCredit(summary.totalWin)}
              </div>
              <div className="text-xs text-muted-foreground">ยอดชนะ</div>
            </CardContent>
          </Card>

          <Card className={cn(
            "bg-gradient-to-br",
            summary.totalProfit >= 0
              ? "from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20"
              : "from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20"
          )}>
            <CardContent className="p-3 text-center">
              {summary.totalProfit >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-600 mx-auto mb-1" />
              )}
              <div className={cn(
                "text-lg font-bold",
                summary.totalProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {summary.totalProfit >= 0 ? '+' : ''}{formatCredit(summary.totalProfit)}
              </div>
              <div className="text-xs text-muted-foreground">กำไร/ขาดทุน</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Provider Stats (if has data) */}
      {summary && Object.keys(summary.byProvider).length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">สถิติตามค่ายเกม</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
              {Object.entries(summary.byProvider)
                .sort((a, b) => b[1].count - a[1].count)
                .slice(0, 8)
                .map(([provider, stats]) => (
                  <div
                    key={provider}
                    className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => setSelectedProvider(provider)}
                  >
                    <div className="font-medium text-sm truncate">{provider}</div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>{stats.count} เกม</span>
                      <span className={cn(
                        stats.profit >= 0 ? "text-green-600" : "text-red-600"
                      )}>
                        {stats.profit >= 0 ? '+' : ''}{formatCredit(stats.profit)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter + Table */}
      <Card>
        <CardHeader className="py-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">รายการเดิมพัน</CardTitle>
            <Select value={selectedProvider} onValueChange={setSelectedProvider}>
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue placeholder="ค่ายเกม" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกค่ายเกม</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : betHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <AlertCircle className="w-8 h-8 mb-2" />
              <p>ไม่พบประวัติการเดิมพัน</p>
              <p className="text-xs mt-1">กดปุ่ม "รีเฟรช" เพื่อดึงข้อมูลล่าสุด</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">เวลา</TableHead>
                      <TableHead>เกม</TableHead>
                      <TableHead>ค่าย</TableHead>
                      <TableHead className="text-right">เดิมพัน</TableHead>
                      <TableHead className="text-right">ชนะ</TableHead>
                      <TableHead className="text-right">กำไร/ขาดทุน</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {betHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(item.bet_time)}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium truncate max-w-[150px]">
                            {item.game_name || '-'}
                          </div>
                          {item.game_type && (
                            <Badge variant="outline" className="text-[10px] mt-0.5">
                              {item.game_type}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {item.provider}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-amber-600">
                          {formatCredit(item.bet_amount)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {formatCredit(item.win_amount)}
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-medium",
                          item.profit >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {item.profit >= 0 ? '+' : ''}{formatCredit(item.profit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {total > limit && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    แสดง {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} จาก {total}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadBetHistory(page - 1)}
                      disabled={page <= 1 || isLoading}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      ก่อนหน้า
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadBetHistory(page + 1)}
                      disabled={!hasMore || isLoading}
                    >
                      ถัดไป
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MemberBetHistoryTab;
